import geoutil from 'leaflet-geometryutil';
import { ensureDayjs, ensureDayjsArray } from './dateUtils';

var hpa_to_alt = (hpa) => {
    return (1 - Math.pow(hpa / 1013.25, 0.190284)) * 44307.69396;
};

function parse_openmeteo_weather(model_data) {
    let out = {}
    for (const [var_name, variable_data] of Object.entries(model_data)) {
        const variable_name = var_name.toLowerCase();
        var numberPattern = /\d+/g;
        var numbers = variable_name.match(numberPattern);
        if (numbers == null || numbers.length != 1) {
            continue;
        }

        var num = Number(numbers[0])

        var alt_m = undefined;
        if (variable_name.includes('hpa')) {
            alt_m = hpa_to_alt(num);
        } else {
            alt_m = num;
        }

        if (!(alt_m in out)) {
            out[alt_m] = {}
        }

        if (variable_name.includes('winddirection')) {
            out[alt_m]['direction'] = variable_data
        } else if (variable_name.includes('windspeed')) {
            out[alt_m]['speed_ms'] = variable_data;
        }
    }

    let out_array = []
    for (const [alt_m, data] of Object.entries(out)) {
        let item = {
            'alt_m': alt_m,
            'direction': data['direction'],
            'speed_ms': data['speed_ms']
        }
        out_array.push(item);
    }

    const comp_fun = (a, b) => {
        return a.alt_m - b.alt_m;
    };

    out_array.sort(comp_fun);

    return out_array.filter((item) => {
        return !isNaN(item.direction[0]) && !isNaN(item.direction[1] && !isNaN(item.speed_ms[0]) && !isNaN(item.speed_ms[1]))
    });
}

function get_interpolation_multiplier(min_max_date, date) {
    // Convert dates to Dayjs objects if they aren't already
    const dates = ensureDayjsArray(min_max_date);
    const targetDate = ensureDayjs(date);
    
    // Use unix timestamps for calculation
    const minTimestamp = dates[0].unix();
    const maxTimestamp = dates[1].unix();
    const targetTimestamp = targetDate.unix();
    
    let span = maxTimestamp - minTimestamp;
    let diff = targetTimestamp - minTimestamp;
    return 1 - diff / span;
}

function interpolate(a, interpolation_multipler) {
    return a[0] * interpolation_multipler + a[1] * (1.0 - interpolation_multipler)
}

function interpolate_Deg(direction_deg, interpolation_multipler) {
    // When we have two wind directions close to 0, for example 5deg and 355deg
    // we need to take care that we do not interpolate to 180deg
    // This can be achieved by converting the directions into vectors first

    let dir_rad = direction_deg.map(x => x * Math.PI / 180);
    let x = dir_rad.map(x => Math.cos(x));
    let y = dir_rad.map(x => Math.sin(x));
    let interpolated_x = interpolate(x, interpolation_multipler);
    let interpolated_y = interpolate(y, interpolation_multipler);
    let interpolated_direction_deg = Math.atan2(interpolated_y, interpolated_x) * 180 / Math.PI;

    return interpolated_direction_deg
}

export function predictFromWeather(predicted_weather_data, date_canopy_fall, fallrate_ms, fallheight_m) {
    if (fallrate_ms < 0.1) {
        return {};
    }
    if (fallheight_m < 1) {
        fallheight_m = 1;
    }
    
    // Ensure date_canopy_fall is a Dayjs object
    const canopyFallDate = ensureDayjs(date_canopy_fall);

    let predictions = {}
    for (const [weather_model_name, weather_data] of Object.entries(predicted_weather_data)) {
        var data = weather_data['hourly'];
        var parsed_data = parse_openmeteo_weather(data);
        if (parsed_data.length == 0) {
            continue;
        }
        // Ensure data.time is an array of Dayjs objects (handles serialized strings from WebSocket)
        var interpolation_multipler = get_interpolation_multiplier(data.time, canopyFallDate);
        var drift_distance_m = [];
        var drift_dir_deg = [];
        var altitudes_m = [];
        var prev_level_m = 0.0;
        parsed_data.forEach((item) => {
            let alt_m = Math.min(item.alt_m, fallheight_m);
            let fall_vertical_m = alt_m - prev_level_m;
            let fall_s = fall_vertical_m / fallrate_ms;
            // + 180 for converting from wind direction to drift direction
            // % 360 to limit the angle to more conventional range
            const dir_deg = (interpolate_Deg(item.direction, interpolation_multipler) + 180) % 360;
            const speed_ms = interpolate(item.speed_ms, interpolation_multipler);
            const distance_m = speed_ms * fall_s;
            //const drift_x = Math.cos(dir_rad)*speed_ms*fall_s;
            //const drift_y = -Math.sin(dir_rad)*speed_ms*fall_s;

            if (fall_s > 0) {
                drift_distance_m.push(distance_m);
                drift_dir_deg.push(dir_deg);
                altitudes_m.push([prev_level_m, alt_m])
            }


            prev_level_m = alt_m;
        });

        predictions[weather_model_name] = { 'drift_dir_deg': drift_dir_deg, 'drift_distance_m': drift_distance_m, 'altitudes_m': altitudes_m };
    }

    return predictions
};

export function predictionToGeojson(predictions: any, input_data: any) {
    const release_spot = input_data['releaseLocation'];

    const get_coords = function (pos) {
        return [pos['lng'], pos['lat']];
    }

    let features = []

    {
        // Add initial position
        const fall_geometry = {
            type: "Point",
            coordinates: get_coords(release_spot),
        };
        const fall_feature = {
            type: "Feature",
            geometry: fall_geometry,
            properties: {
                type: 'cutaway_location'
            }
        };
        features.push(fall_feature);
    }

    for (const [model_name, prediction_data] of Object.entries(predictions)) {
        const drift_dir_deg = prediction_data['drift_dir_deg'];
        const drift_distance_m = prediction_data['drift_distance_m'];

        if (drift_dir_deg === null || drift_distance_m === null || drift_dir_deg.length != drift_distance_m.length) {
            continue;
        }

        var current_pos = release_spot;

        const num_predictions = drift_dir_deg.length;
        var next_spot;

        let positions = [get_coords(current_pos)]
        for (var i = num_predictions - 1; i >= 0; i--) {
            const heading_degrees = drift_dir_deg[i];
            const distance_m = drift_distance_m[i];
            next_spot = geoutil.destination(current_pos, heading_degrees, distance_m);
            positions.push(get_coords(next_spot));
            current_pos = next_spot;
        }

        const geometry = {
            type: "LineString",
            coordinates: positions,
        }
        const feature = {
            type: "Feature",
            geometry: geometry,
            properties: {
                type: "prediction_path",
                model_name: model_name
            }
        };
        features.push(feature);

        const fall_geometry = {
            type: "Point",
            coordinates: get_coords(next_spot),
        };
        const fall_feature = {
            type: "Feature",
            geometry: fall_geometry,
            properties: {
                type: "prediction_landing",
                model_name: model_name
            }
        };
        features.push(fall_feature);
    }

    const geojson = {
        type: "FeatureCollection",
        name: "predicted_canopy_positions",
        crs: {
            type: "name",
            properties: {
                name: "urn:ogc:def:crs:OGC:1.3:CRS84",
            },
        },
        features: features,
    };
    return geojson;
}